import React, { useCallback } from "react";
import { TOOL_PREP_KEYS } from "../../../../utils/editorEntrySession";
import { mergeToolPrepSubjectPlacement } from "../../lib/toolPrepSubjectPlacement";
import ResizeToolPrep from "../../layout/resize/Popup/ResizeToolPrep";
import ShadowToolPrep from "../../object/shadow/Popup/ShadowToolPrep";
import OutlineToolPrep from "../../object/outline/Popup/OutlineToolPrep";
import BackgroundColorToolPrep from "../../background/backgroundColor/Popup/BackgroundColorToolPrep";
import { WhiteBackgroundToolPrep } from "../../background/whiteBackground/Popup/WhiteBackgroundToolPrep";
import { BlackBackgroundToolPrep } from "../../background/blackBackground/Popup/BlackBackgroundToolPrep";
import RotateToolPrep from "../../object/rotate/Popup/RotateToolPrep";
import FlipToolPrep from "../../object/flip/Popup/FlipToolPrep";
import PaddingToolPrep from "../../layout/padding/Popup/PaddingToolPrep";
import CropToolPrep from "../../layout/crop/Popup/CropToolPrep";
import CircleCropToolPrep from "../../layout/circleCrop/Popup/CircleCropToolPrep";
import WatermarkToolPrep from "../../watermark/Popup/WatermarkToolPrep";
import BlurToolPrep from "../../background/blur/Popup/BlurToolPrep";
import PdfWatermarkerToolPrep from "../../document/pdfWatermarker/Popup/PdfWatermarkerToolPrep";
import WordWatermarkerToolPrep from "../../document/wordWatermarker/Popup/WordWatermarkerToolPrep";
import PdfPageNumbersToolPrep from "../../document/pdfPageNumbers/Popup/PdfPageNumbersToolPrep";
import SignPdfToolPrep from "../../document/signPdf/Popup/SignPdfToolPrep";
import OutpaintToolPrep from "../../ai/outpaint/Popup/OutpaintToolPrep";
import RecolorToolPrep from "../../ai/recolor/Popup/RecolorToolPrep";
import EnhanceToolPrep from "../../ai/enhance/Popup/EnhanceToolPrep";
import BackgroundImageToolPrep from "../../ai/backgroundImage/Popup/BackgroundImageToolPrep";
import AiShadowToolPrep from "../../ai/shadow/Popup/AiShadowToolPrep";
import RemoveObjectToolPrep from "../../ai/removeObject/Popup/RemoveObjectToolPrep";
import RemoveTextToolPrep from "../../ai/removeText/Popup/RemoveTextToolPrep";
import UpscaleToolPrep from "../../ai/upscale/Popup/UpscaleToolPrep";
import RelightToolPrep from "../../ai/relight/Popup/RelightToolPrep";
import RemoveWatermarkToolPrep from "../../ai/removeWatermark/Popup/RemoveWatermarkToolPrep";
import BackgroundRemoverToolPrep from "../../ai/backgroundRemover/Popup/BackgroundRemoverToolPrep";
import ShadowRemoverToolPrep from "../../ai/shadowRemover/Popup/ShadowRemoverToolPrep";
import OldPhotoRestoreToolPrep from "../../ai/oldPhotoRestore/Popup/OldPhotoRestoreToolPrep";
import ColorizerToolPrep from "../../ai/colorizer/Popup/ColorizerToolPrep";
import UnblurToolPrep from "../../ai/unblur/Popup/UnblurToolPrep";
import ReplaceObjectToolPrep from "../../ai/replaceObject/Popup/ReplaceObjectToolPrep";
import ReplaceTextToolPrep from "../../ai/replaceText/Popup/ReplaceTextToolPrep";
import FaceSwapToolPrep from "../../ai/faceSwap/Popup/FaceSwapToolPrep";
import AiPassportPhotoMakerToolPrep from "../../AiPhotoStudio/aiPassportPhotoMaker/Popup/AiPassportPhotoMakerToolPrep";
import ImageConvertToolPrep from "../../convert/Popup/ImageConvertToolPrep";

/**
 * Renders the matching tool-prep overlay for a toolKey.
 * Used by the /tools/:toolKey workspace and can share props with Editor overlays.
 */
const ToolPrepSwitch = ({
  toolKey,
  originalImageUrl = null,
  imageFile = null,
  onDone = () => {},
  onCancel = () => {},
  removeBgDefault = false,
  cachedCutoutUrl = null,
  onCutoutReady = null,
}) => {
  const handoffDone = useCallback(
    (result) => onDone(mergeToolPrepSubjectPlacement(result)),
    [onDone]
  );
  const imageProps = { originalImageUrl, imageFile, onDone: handoffDone, onCancel };
  const cutoutProps = {
    ...imageProps,
    cachedCutoutUrl,
    onCutoutReady,
  };
  const aiCutoutProps = {
    ...cutoutProps,
    removeBgDefault,
  };

  switch (toolKey) {
    case TOOL_PREP_KEYS.RESIZE:
      return (
        <ResizeToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.SHADOW:
      return <ShadowToolPrep {...cutoutProps} />;
    case TOOL_PREP_KEYS.OUTLINE:
      return <OutlineToolPrep {...cutoutProps} />;
    case TOOL_PREP_KEYS.BACKGROUND_COLOR:
      return <BackgroundColorToolPrep {...cutoutProps} />;
    case TOOL_PREP_KEYS.WHITE_BACKGROUND:
      return <WhiteBackgroundToolPrep {...cutoutProps} />;
    case TOOL_PREP_KEYS.BLACK_BACKGROUND:
      return <BlackBackgroundToolPrep {...cutoutProps} />;
    case TOOL_PREP_KEYS.ROTATE:
      return (
        <RotateToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.FLIP:
      return (
        <FlipToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.PADDING:
      return (
        <PaddingToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.CROP:
      return (
        <CropToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.CIRCLE_CROP:
      return (
        <CircleCropToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.WATERMARK_MAKER:
      return (
        <WatermarkToolPrep {...cutoutProps} removeBgDefault={removeBgDefault} />
      );
    case TOOL_PREP_KEYS.BLUR:
    case TOOL_PREP_KEYS.GAUSSIAN_BLUR:
    case TOOL_PREP_KEYS.PIXEL_BLUR:
    case TOOL_PREP_KEYS.MOTION_BLUR:
    case TOOL_PREP_KEYS.BOKEH_BLUR:
    case TOOL_PREP_KEYS.BACKGROUND_BLUR:
      return <BlurToolPrep {...aiCutoutProps} toolKey={toolKey} />;
    case TOOL_PREP_KEYS.PDF_WATERMARKER:
      return <PdfWatermarkerToolPrep onCancel={onCancel} onDone={onDone} />;
    case TOOL_PREP_KEYS.WORD_WATERMARKER:
      return <WordWatermarkerToolPrep onCancel={onCancel} onDone={onDone} />;
    case TOOL_PREP_KEYS.PDF_PAGE_NUMBERS:
      return <PdfPageNumbersToolPrep onCancel={onCancel} onDone={onDone} />;
    case TOOL_PREP_KEYS.SIGN_PDF:
      return <SignPdfToolPrep onCancel={onCancel} onDone={onDone} />;
    case TOOL_PREP_KEYS.AI_EXPAND:
      return <OutpaintToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_RECOLOR:
      return <RecolorToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_ENHANCE:
      return <EnhanceToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_BACKGROUND_IMAGE:
      return <BackgroundImageToolPrep {...aiCutoutProps} />;
    case TOOL_PREP_KEYS.AI_SHADOW:
      return <AiShadowToolPrep {...aiCutoutProps} />;
    case TOOL_PREP_KEYS.AI_REMOVE_OBJECT:
      return <RemoveObjectToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_REMOVE_TEXT:
      return <RemoveTextToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_UPSCALE:
      return <UpscaleToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_RELIGHT:
      return <RelightToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_REMOVE_WATERMARK:
      return <RemoveWatermarkToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_BACKGROUND_REMOVER:
      return <BackgroundRemoverToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_SHADOW_REMOVER:
      return <ShadowRemoverToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_OLD_PHOTO_RESTORE:
      return <OldPhotoRestoreToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_COLORIZER:
      return <ColorizerToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_UNBLUR:
      return <UnblurToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_REPLACE_OBJECT:
      return <ReplaceObjectToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_REPLACE_TEXT:
      return <ReplaceTextToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_FACE_SWAP:
      return <FaceSwapToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.AI_PASSPORT_PHOTO_MAKER:
      return <AiPassportPhotoMakerToolPrep {...imageProps} />;
    case TOOL_PREP_KEYS.HEIC_TO_JPG:
    case TOOL_PREP_KEYS.WEBP_TO_JPG:
    case TOOL_PREP_KEYS.PNG_TO_JPG:
    case TOOL_PREP_KEYS.JPG_TO_PNG:
    case TOOL_PREP_KEYS.WEBP_TO_PNG:
      return (
        <ImageConvertToolPrep
          toolKey={toolKey}
          onDone={onDone}
          onCancel={onCancel}
        />
      );
    default:
      return null;
  }
};

export default ToolPrepSwitch;
